import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '@gg/shared';
import { siteLayout } from '@gg/shared/i18n';
import { ConsentBanner } from '@/components/ConsentBanner';
import { consentEnabled, reopenConsent } from '@/lib/consent';
import { setLocale } from '@/lib/i18n';
import { useCaptureReferralCode } from '@/lib/referral';
import { ArrowIcon, CloseIcon, MenuIcon } from './siteIcons';
import { ctaClasses, fill, useSiteContent } from './siteHelpers';

export const SITE_LOGO = '/site/logo.png';

/**
 * Public site shell (/, /conditions, /reglement): sticky dark header with the main navigation,
 * FR/EN switch and "Je m'engage" (→ format selection), a menu sheet on phones, and the footer.
 */
export function SiteLayout() {
  const c = useSiteContent(siteLayout);
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useCaptureReferralCode();

  // In-page anchors (/#epreuves) across routes: scroll once the page has rendered.
  useEffect(() => {
    setMenuOpen(false);
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    const frame = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return (
    <div className="flex min-h-dvh flex-col bg-site-ground font-site text-site-ink antialiased">
      <ConsentBanner />
      <header className="sticky top-0 z-30 border-b border-site-cream/10 bg-site-night/90 backdrop-blur-md">
        <div className="flex h-[76px] items-center justify-between gap-6 px-4 md:px-10">
          <Link to="/" aria-label={c.homeLabel} className="flex items-center">
            <img src={SITE_LOGO} alt="Gravel Games" className="block h-6 w-auto md:h-7.5" />
          </Link>
          <nav
            aria-label={c.navLabel}
            className="hidden items-center gap-7.5 font-site-display text-sm font-medium tracking-[0.14em] uppercase lg:flex"
          >
            {c.nav.map((n) => (
              <NavItem key={n.to} to={n.to} label={n.label} />
            ))}
          </nav>
          <div className="flex items-center gap-4.5">
            <LanguageLinks className="hidden lg:flex" label={c.language} />
            <Link to="/formats" className={ctaClasses('clay', 'sm')}>
              {c.engage}
              <ArrowIcon size={16} />
            </Link>
            <button
              type="button"
              aria-label={menuOpen ? c.closeMenu : c.openMenu}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex size-11 items-center justify-center border border-site-cream/30 text-site-cream lg:hidden"
            >
              {menuOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav
            id="site-menu"
            aria-label={c.navLabel}
            className="border-t border-site-cream/10 px-5 pt-2 pb-6 lg:hidden"
          >
            <ul className="m-0 flex list-none flex-col p-0">
              {c.nav.map((n) => (
                <li key={n.to} className="border-b border-site-cream/10">
                  <Link
                    to={n.to}
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-13 items-center font-site-display text-lg tracking-[0.12em] text-site-cream uppercase no-underline hover:text-site-clay-light"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
              <li className="border-b border-site-cream/10">
                <Link
                  to="/me"
                  className="flex min-h-13 items-center font-site-display text-lg tracking-[0.12em] text-site-cream/75 uppercase no-underline hover:text-site-clay-light"
                >
                  {c.account}
                </Link>
              </li>
            </ul>
            <LanguageLinks className="mt-5 flex" label={c.language} />
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-site-night-deep px-5 pt-14 pb-10 text-sm text-site-cream/70 md:px-10">
        <div className="mx-auto flex max-w-[1240px] flex-wrap justify-between gap-8">
          <div>
            <img src={SITE_LOGO} alt="Gravel Games" className="block h-9 w-auto" />
            <p className="m-0 mt-2.5">{c.tagline}</p>
          </div>
          <nav aria-label={c.footLabel} className="flex flex-wrap gap-x-7 gap-y-3.5">
            {c.foot.map((f) => (
              <Link key={f.to} to={f.to} className="text-site-cream/75 hover:text-site-cream">
                {f.label}
              </Link>
            ))}
            <Link to="/me" className="text-site-cream/75 hover:text-site-cream">
              {c.account}
            </Link>
          </nav>
        </div>
        <div className="mx-auto mt-10 flex max-w-[1240px] flex-wrap justify-between gap-3 border-t border-site-cream/10 pt-5.5 text-[13px]">
          <span>{fill(c.copyright, { year: new Date().getFullYear() })}</span>
          {consentEnabled && <ConsentLink />}
        </div>
      </footer>
    </div>
  );
}

function ConsentLink() {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={reopenConsent}
      className="text-site-cream/65 underline hover:text-site-cream"
    >
      {t('consent.manage')}
    </button>
  );
}

/** Route links get `aria-current`; in-page anchors (/#…) never look "current". */
function NavItem({ to, label }: { to: string; label: string }) {
  const cls = 'text-site-cream/80 no-underline hover:text-site-cream';
  if (to.includes('#')) {
    return (
      <Link to={to} className={cls}>
        {label}
      </Link>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) => (isActive ? `${cls} text-site-clay-light!` : cls)}
    >
      {label}
    </NavLink>
  );
}

function LanguageLinks({ className = '', label }: { className?: string; label: string }) {
  const { i18n } = useTranslation();
  return (
    <div
      role="group"
      aria-label={label}
      className={`items-center gap-1 font-site-display text-xs tracking-[0.16em] ${className}`}
    >
      {SUPPORTED_LOCALES.map((l, i) => {
        const active = i18n.language === l;
        return (
          <span key={l} className="flex items-center gap-1">
            {i > 0 && <span className="text-site-cream/35">/</span>}
            <button
              type="button"
              lang={l}
              aria-pressed={active}
              onClick={() => setLocale(l)}
              className={`min-h-11 px-1.5 uppercase ${active ? 'text-site-clay-light' : 'text-site-cream/65 hover:text-site-cream'}`}
            >
              {l}
            </button>
          </span>
        );
      })}
    </div>
  );
}
